import { useState } from 'react';
import { usePreProcessingMetrics } from '../../hooks/usePreProcessingMetrics';
import { useResumeCollection } from '../../hooks/useCollections';
import { openCreditCardDialog, broadcastBasicSearch } from '../../services/angular-bridge';
import { formatBytes } from '../../utilities/format';
import { PieChartPanel } from '../../components/charts/PieChartPanel';
import { LiquidGauge } from '../../components/charts/LiquidGauge';
import { LoadingPlaceholder } from '../../components/LoadingPlaceholder';
import type { Collection } from '../../types/collection';
import type { Project } from '../../types/project';

type DataSelection = 'Extension' | 'Category';

/**
 * Per-collection Pre-Processing overview — the React port of collection.html's first tab
 * (CollectionController). Reuses the shared LiquidGauge + PieChartPanel; adds the
 * collection-specific processing-status strip, progress bar, and paused/initializing
 * alerts that the project-level PreProcessingTab doesn't have. Gauges + status come from
 * the collection detail (polled while processing); the data-overview pies from the
 * collection-scoped pre-processing metrics.
 */
export function CollectionPreProcessingTab({
  projectId,
  collectionId,
  collection,
  project,
}: {
  projectId: string;
  collectionId: string;
  collection?: Collection;
  project?: Project;
}) {
  const { data: metrics, isLoading } = usePreProcessingMetrics(projectId, collectionId);
  const resume = useResumeCollection(projectId);
  const [selection, setSelection] = useState<DataSelection>('Extension');

  const status = collection?.status ?? null;
  const isProcessing = status === 'Processing';
  const isInitializing = !status || status === 'Initializing' || status === 'New';
  const isPaused = status === 'Paused';
  const isTrial = project?.status === 'trial';

  const sourceBytes = collection?.processingSource?.bytes ?? 0;
  const outputBytes = collection?.processingOutput?.bytes ?? 0;
  const totalBytes = sourceBytes + outputBytes;

  const num = (n: number | null | undefined) => (n ?? 0).toLocaleString();
  const processedCount =
    (collection?.numProcessed ?? 0) -
    Math.max(collection?.pdfQueued ?? 0, collection?.ocrQueued ?? 0, collection?.audioQueued ?? 0);

  const sizeData = selection === 'Extension' ? (metrics?.extensionBySize ?? []) : (metrics?.typeBySize ?? []);
  const countData = selection === 'Extension' ? (metrics?.extensionByCount ?? []) : (metrics?.typeByCount ?? []);

  function handleSearch(label: string) {
    broadcastBasicSearch(selection === 'Extension' ? `fileExtension:"${label}"` : `category:"${label}"`, 0);
  }

  return (
    <>
      <h2><i className="far fa-lightbulb" aria-hidden="true" /> {collection?.name} Overview</h2>

      {/* Processing-status label strip (while queued/initializing/processing). */}
      {(isProcessing || isInitializing) && (
        <p>
          <span className="label label-default">{num(collection?.numQueued)} Queued</span>{' '}
          <span className="label label-default">{num(collection?.pdfQueued)} Pending PDF</span>{' '}
          <span className="label label-default">{num(collection?.nlpQueued)} Pending NLP</span>{' '}
          <span className="label label-default">{num(collection?.ocrQueued)} Pending OCR</span>{' '}
          <span className="label label-default">{num(collection?.audioQueued)} Pending Transcription</span>{' '}
          <span className="label label-success">{processedCount.toLocaleString()} Processed</span>{' '}
          <span className="label label-info">{formatBytes(totalBytes)}</span>
        </p>
      )}

      {isProcessing && collection?.job && (
        <div className="progress">
          <div className="progress-bar progress-bar-striped active" style={{ width: `${collection.job.percentageComplete}%` }}>
            {collection.job.percentageComplete}%
          </div>
        </div>
      )}

      {isInitializing && (
        <div className="alert alert-info" role="alert">
          <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Initializing Processors — we are
          initializing a dedicated server environment to process your data.
        </div>
      )}

      {isPaused && isTrial && (
        <div className="alert alert-warning" role="alert">
          <h4>Processing Paused</h4>
          <p>Your trial processing quota has been exceeded. Activate the project to continue.</p>
          <button type="button" className="btn btn-primary" onClick={() => project && openCreditCardDialog(project)}>
            Activate
          </button>
        </div>
      )}

      {isPaused && !isTrial && (
        <div className="alert alert-warning" role="alert">
          <h4>Processing Paused</h4>
          <button type="button" className="btn btn-primary" disabled={resume.isPending}
            onClick={() => resume.mutate(collectionId)}>
            Resume Processing
          </button>
        </div>
      )}

      {status === 'Processed' && (collection?.bytesProcessed ?? 0) > 0 && (
        <p className="lead">
          Processed {num(collection?.numProcessed)} documents ({formatBytes(collection?.bytesProcessed ?? 0)})
        </p>
      )}

      {/* Source / output liquid gauges. */}
      {totalBytes > 0 && (
        <div className="row">
          <div className="col-md-5 text-center">
            <h2>Pre-Processing</h2>
            <LiquidGauge value={sourceBytes} total={totalBytes} />
          </div>
          <div className="col-md-2 text-center hidden-xs" style={{ marginTop: 150, marginLeft: -25 }}>
            <i className="fas fa-arrow-right" style={{ fontSize: 128, opacity: 0.07 }} aria-hidden="true" />
          </div>
          <div className="col-md-5 text-center">
            <h2>Post-Processing</h2>
            <LiquidGauge value={totalBytes} total={totalBytes} />
          </div>
        </div>
      )}

      {/* Data Overview (Extension / Category pies). */}
      <div style={{ marginTop: 40 }}>
        <h2>Data Overview</h2>
        <div className="btn-group">
          {(['Extension', 'Category'] as DataSelection[]).map((s) => (
            <label key={s} className={`btn btn-default${selection === s ? ' active' : ''}`} onClick={() => setSelection(s)}>
              {s}
            </label>
          ))}
        </div>
        {isLoading ? (
          <LoadingPlaceholder />
        ) : (
          metrics && (
            <div style={{ marginTop: 44 }}>
              <PieChartPanel key={`size-${selection}`} title={`${selection} by Size`} data={sizeData} valueType="bytes" onItemClick={handleSearch} />
              <div className="col-md-2" />
              <PieChartPanel key={`count-${selection}`} title={`${selection} by Count`} data={countData} valueType="count" onItemClick={handleSearch} />
            </div>
          )
        )}
      </div>
    </>
  );
}
